import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * FeedUrlPanel — mostra le URL del feed XML dell'agente (1 per portale).
 *
 * Si incolla una volta nel backoffice del portale → tutti gli annunci nuovi
 * vengono pubblicati automaticamente, le modifiche si propagano al prossimo fetch.
 *
 * iter v29 r219 — User M1548: solo Idealista + Immobiliare.it sono supportati
 * con feed XML reale; Casa.it e Subito non hanno specifica feed pubblica
 * affidabile e venivano serviti come "generic" → rischio rifiuto. Rimossi.
 *
 * Visibile sia su PropertyDetail (tab Portali) sia eventualmente su /profilo.
 */
const PORTALS = [
  // iter v29 r222 — Colori brand reali da logo ufficiali condivisi dall'utente:
  // Idealista verde-lime fluorescente, Immobiliare.it blu brand.
  { key: "idealista", label: "Idealista", color: "#D7F600" },
  { key: "immobiliare", label: "Immobiliare.it", color: "#1F5AAF" },
  { key: "generic", label: "Generico (MLS)", color: "#7C8B6E" },
];

export default function FeedUrlPanel() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/feed/info");
      setInfo(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => { reload(); }, []);

  const copy = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(`URL ${label} copiato`);
  };

  const regenerate = async () => {
    if (!window.confirm("Generare un nuovo URL? Il vecchio smetterà di funzionare e dovrai re-incollarlo nei portali.")) return;
    setBusy(true);
    try {
      await api.post("/feed/regenerate");
      await reload();
      toast.success("Nuovo URL generato");
    } catch {
      toast.error("Errore");
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;

  return (
    <div id="feed-url-panel" className="card-flat p-6 mt-2" data-testid="feed-url-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <span className="overline">I tuoi URL · 1 per portale</span>
          <h3 className="font-editorial text-xl mt-2">Copia l'URL del tuo portale</h3>
        </div>
        <button
          onClick={regenerate}
          disabled={busy}
          className="text-[12px] text-[#6B6A65] hover:text-[#7C8B6E] inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="feed-regen-btn"
          title="Rigenera URL · il vecchio smette di funzionare"
        >
          <ArrowsClockwise size={13} /> Rigenera
        </button>
      </div>

      <div className="space-y-2">
        {PORTALS.map(({ key, label, color }) => (
          <div
            key={key}
            className="flex items-center justify-between gap-3 bg-[#FAF9F4] border border-[#EAE7DF] rounded-lg px-4 py-3"
            data-testid={`feed-portal-${key}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span
                className="w-2 h-8 rounded-full shrink-0"
                style={{ background: color }}
              />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#1C1C1A]">{label}</div>
                <div className="text-[11px] text-[#6B6A65] truncate font-mono">
                  {info.urls[key]}
                </div>
              </div>
            </div>
            <button
              onClick={() => copy(info.urls[key], label)}
              className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#7C8B6E] hover:text-white border border-[#7C8B6E] hover:bg-[#7C8B6E] rounded-lg px-3 py-1.5 transition-colors"
              data-testid={`feed-copy-${key}`}
            >
              <Copy size={12} /> Copia
            </button>
          </div>
        ))}
      </div>

      {info.properties_in_feed === 0 && (
        <div className="mt-4 text-[12px] bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
          Nessun annuncio nel feed ancora. Rendi online almeno un annuncio (bottone "Rendi annuncio online") perché il portale veda qualcosa.
        </div>
      )}
    </div>
  );
}
